import Fuse from 'fuse.js';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import { useDownload } from '@/hooks/useDownload';
import { useGetBlobsQuery, useGetCollectionTreeQuery } from '@/services/api';
import { blobsApiClient } from '@/services/api/blobService';
import { blobSinkActionService } from '@/services/api/blobSinkActionService';
import { collectionsApiClient } from '@/services/api/collectionService';
import { BlobSinkActionsTreeApiArg } from '@/services/api/generated/blobSinkActionService';
import { useFirmsBlobSinkConfigsListQuery } from '@/services/api/generated/blobSinkConfigService';
import { useGetGoogleDriveStatusQuery } from '@/services/api/googleDriveService';
import {
  useGetMatterCompanionsQuery,
  useGetMatterQuery,
} from '@/services/api/mattersService';
import { Blob } from '@/services/types/blob-types';
import {
  getCmsTargetSystemFromUrl,
  getDriveSystemFromUrl,
  getExternalSystemDisplayName,
} from '@/utils/externalSystemUtils';

import { Button } from '../ui/button';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '../ui/resizable';
import { ActionBar } from './components/ActionBar';
import { ExternalSystemTreeView } from './components/ExternalSystemTreeView';
import { DocumentInspector } from './components/inspector/DocumentInspector';
import { SortButton, SortConfig } from './components/SortButton';
import { TreeView } from './components/TreeView';
import {
  DocumentTreeProvider,
  useDocumentTree,
} from './context/DocumentTreeContext';
import { useUploadManagerUnified } from './hooks/useUploadManagerUnified';

export const DocumentExplorer = () => {
  const { matter_id } = useParams<{ matter_id: string }>();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: collectionTree, refetch: refetchCollections } =
    useGetCollectionTreeQuery(matter_id || '', {
      skip: !matter_id,
    });

  const { data: blobs, refetch: refetchBlobs } = useGetBlobsQuery(
    matter_id || '',
    {
      skip: !matter_id,
      pollingInterval: 30000,
      skipPollingIfUnfocused: true,
    },
  );

  const { data: companionsData } = useGetMatterCompanionsQuery(
    matter_id || '',
    {
      skip: !matter_id,
    },
  );

  // Fetch matter data for pinning functionality
  const { data: matter } = useGetMatterQuery(matter_id || '', {
    skip: !matter_id,
  });

  // Detect CMS type from matter's external_cms_url (CMS systems only — not Google Drive)
  const detectedCmsType = getCmsTargetSystemFromUrl(matter?.external_cms_url);
  // Detect drive storage system from matter's external_drive_url
  const detectedDriveType = getDriveSystemFromUrl(matter?.external_drive_url);

  const firmId = matter?.firm?.id;

  // Fetch firm-level blob sink configs to check if doc sync is enabled for the detected CMS
  const { data: blobSinkConfigs } = useFirmsBlobSinkConfigsListQuery(
    { firmId: firmId || '' },
    { skip: !firmId },
  );

  // Check if doc sync is enabled for the detected CMS type
  const isDocSyncEnabledForCms = detectedCmsType
    ? blobSinkConfigs?.some(
        (config) =>
          config.target_system === detectedCmsType && config.is_enabled,
      ) ?? false
    : false;

  // Check firm-level Google Drive connection so we can show the tab even before first sync.
  // Skip when matter has an explicit drive URL because the matter is already pinned to a drive provider.
  const { data: googleDriveStatus } = useGetGoogleDriveStatusQuery(
    { firmId: firmId || '' },
    { skip: !firmId || !!detectedDriveType },
  );
  const isGoogleDriveConnected = !!googleDriveStatus?.connected;

  // Pre-fetch CMS tree data so ExternalSystemTreeView has it cached when the tab is opened.
  // Note: Cast needed because generated types may not include all systems yet
  blobSinkActionService.useBlobSinkActionsTreeQuery(
    {
      matterId: matter_id || '',
      targetSystem: detectedCmsType || 'FILEVINE',
    } as BlobSinkActionsTreeApiArg,
    { skip: !matter_id || !isDocSyncEnabledForCms },
  );

  // Check for Dropbox when the matter is linked to Dropbox or has Dropbox tree data.
  // Cast through unknown because generated types don't include DROPBOX yet.
  const { data: dropboxTreeData } =
    blobSinkActionService.useBlobSinkActionsTreeQuery(
      {
        matterId: matter_id || '',
        targetSystem: 'DROPBOX',
      } as unknown as BlobSinkActionsTreeApiArg,
      {
        skip:
          !matter_id ||
          detectedDriveType === 'GOOGLE_DRIVE',
      },
    );

  const hasDropboxData =
    (dropboxTreeData?.root?.length ?? 0) > 0 ||
    !!dropboxTreeData?.external_matter_id;

  // Google Drive tree: skip when the matter is explicitly linked to Dropbox
  // or Dropbox already has synced tree data.
  const { data: googleDriveTreeData } =
    blobSinkActionService.useBlobSinkActionsTreeQuery(
      {
        matterId: matter_id || '',
        targetSystem: 'GOOGLE_DRIVE',
      } as unknown as BlobSinkActionsTreeApiArg,
      {
        skip:
          !matter_id ||
          detectedDriveType === 'DROPBOX' ||
          hasDropboxData,
      },
    );

  // Determine the drive system to display (Dropbox and Google Drive are mutually exclusive):
  const driveTreeData = hasDropboxData ? dropboxTreeData : googleDriveTreeData;
  let driveTargetSystem: typeof detectedCmsType = detectedDriveType;
  if (!driveTargetSystem) {
    if (hasDropboxData) {
      driveTargetSystem = 'DROPBOX';
    } else if (isGoogleDriveConnected) {
      driveTargetSystem = 'GOOGLE_DRIVE';
    }
  }

  // Determine which tabs are available
  const isCmsTabAvailable = isDocSyncEnabledForCms;
  const isDriveTabAvailable =
    !!detectedDriveType ||
    !!driveTreeData?.external_matter_id ||
    (driveTreeData?.root?.length ?? 0) > 0 ||
    isGoogleDriveConnected;
  const hasAnyExternalTab = isCmsTabAvailable || isDriveTabAvailable;

  // Display names for each tab
  const cmsDisplayName = detectedCmsType
    ? getExternalSystemDisplayName(detectedCmsType)
    : 'External CMS';
  const driveDisplayName = driveTargetSystem
    ? getExternalSystemDisplayName(driveTargetSystem)
    : 'Drive';

  // Combined refetch function for both data sources with cache invalidation
  const refetchAll = useCallback(async () => {
    if (matter_id) {
      // Invalidate the collections cache
      dispatch(
        collectionsApiClient.util.invalidateTags([
          { type: 'Collections', id: matter_id },
        ]),
      );

      // Invalidate the blobs cache
      dispatch(
        blobsApiClient.util.invalidateTags([{ type: 'Blobs', id: matter_id }]),
      );
    }

    // Call refetch functions after invalidating caches
    await Promise.all([refetchBlobs(), refetchCollections()]);
  }, [dispatch, matter_id, refetchBlobs, refetchCollections]);

  const [isDragging, setIsDragging] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'local' | 'cms' | 'drive'>('local');

  // Reset activeTab to 'local' when the currently selected tab becomes unavailable
  useEffect(() => {
    if (activeTab === 'cms' && !isCmsTabAvailable) {
      setActiveTab('local');
    } else if (activeTab === 'drive' && !isDriveTabAvailable) {
      setActiveTab('local');
    }
  }, [activeTab, isCmsTabAvailable, isDriveTabAvailable]);

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'default',
    direction: 'asc',
  });

  // Use our new upload manager hook
  const {
    uploadProgress,
    uploadError,
    handleDrop: handleUploadDrop,
    dismissError,
    clearUploads,
    uploadDialogOpen,
    uploadDialogTargetId,
    showUploadDialog,
    closeUploadDialog,
    handleUploadToCollection,
  } = useUploadManagerUnified({
    matterId: matter_id,
    refetch: refetchAll,
  });

  const rootNodeId = collectionTree?.id;

  // Handler for file drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent, targetCollectionId?: string) => {
    e.preventDefault();
    setIsDragging(false);

    if (targetCollectionId) {
      handleUploadDrop(e, targetCollectionId);
    } else if (rootNodeId) {
      handleUploadDrop(e, rootNodeId);
    }
  };
  const {
    downloadProgress,
    error: downloadError,
    isGeneratingZip,
    clearError: dismissDownloadError,
    clearCompletedDownloads,
    downloadFiles,
  } = useDownload();

  // Create a fuse instance for search
  const fuse = useMemo(() => {
    if (!blobs) return null;
    return new Fuse(blobs, {
      keys: ['name'],
      threshold: 0.3,
      ignoreLocation: true,
    });
  }, [blobs]);

  // Callback for tree operations
  const handleTreeOperationSuccess = useCallback(() => {
    refetchAll();
  }, [refetchAll]);

  // Show loading state if data isn't ready
  if (!collectionTree) {
    return <div className="py-1 px-2 text-[11px]">Loading documents...</div>;
  }

  return (
    <DocumentTreeProvider
      rootNodeId={rootNodeId}
      collectionTree={collectionTree}
    >
      <BlobFocusHandler
        blobs={blobs}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
      />
      <div className="flex flex-col h-full overflow-hidden pl-2">
        {/* Action Bar Component */}
        <ActionBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          blobs={blobs}
          rootNodeId={rootNodeId}
          refetch={refetchAll}
          showUploadDialog={showUploadDialog}
          collectionTree={collectionTree}
          uploadProgress={uploadProgress}
          uploadError={uploadError}
          dismissUploadError={dismissError}
          clearUploads={clearUploads}
          downloadProgress={downloadProgress}
          downloadError={downloadError}
          isGeneratingZip={isGeneratingZip}
          dismissDownloadError={dismissDownloadError}
          clearCompletedDownloads={clearCompletedDownloads}
          uploadDialogOpen={uploadDialogOpen}
          uploadDialogTargetId={uploadDialogTargetId}
          closeUploadDialog={closeUploadDialog}
          handleUploadToCollection={handleUploadToCollection}
          downloadFiles={downloadFiles}
          companions={companionsData?.members}
          matterName={matter?.name}
        />

        {/* Document Tree with independent scrolling */}
        <ResizablePanelGroup direction="horizontal" className="h-full">
          <ResizablePanel defaultSize={20} minSize={15}>
            <div className="flex flex-col h-full">
              {/* Tab switcher and expand/collapse buttons */}
              <div className="flex border-b bg-background items-center">
                {hasAnyExternalTab && (
                  <div className="flex flex-1">
                    <button
                      type="button"
                      onClick={() => setActiveTab('local')}
                      className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                        activeTab === 'local'
                          ? 'text-primary border-b-2 border-primary'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Local Files
                    </button>
                    {isCmsTabAvailable && (
                      <button
                        type="button"
                        onClick={() => setActiveTab('cms')}
                        className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                          activeTab === 'cms'
                            ? 'text-primary border-b-2 border-primary'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {cmsDisplayName}
                      </button>
                    )}
                    {isDriveTabAvailable && (
                      <button
                        type="button"
                        onClick={() => setActiveTab('drive')}
                        className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                          activeTab === 'drive'
                            ? 'text-primary border-b-2 border-primary'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {driveDisplayName}
                      </button>
                    )}
                  </div>
                )}
                {/* Sort and Expand/Collapse buttons - only show for local files tab */}
                {(activeTab === 'local' || !hasAnyExternalTab) && (
                  <div className="flex items-center ml-auto">
                    <TreeExpandCollapseButtons />
                    <SortButton
                      sortConfig={sortConfig}
                      onSortChange={setSortConfig}
                    />
                  </div>
                )}
              </div>

              {/* Tree content */}
              <div className="flex-1 overflow-hidden">
                {activeTab === 'local' || !hasAnyExternalTab
                  ? collectionTree && (
                      <TreeView
                        rootNode={collectionTree}
                        blobs={blobs}
                        searchQuery={searchQuery}
                        handleDragOver={handleDragOver}
                        handleDragLeave={handleDragLeave}
                        handleDrop={handleDrop}
                        isDragging={isDragging}
                        onSuccess={handleTreeOperationSuccess}
                        fuse={fuse}
                        size="sm"
                        matterId={matter_id}
                        sortConfig={sortConfig}
                      />
                    )
                  : activeTab === 'cms' &&
                      matter_id &&
                      detectedCmsType && (
                        <ExternalSystemTreeView
                          matterId={matter_id}
                          targetSystem={detectedCmsType}
                        />
                      )}
                {activeTab === 'drive' &&
                  matter_id &&
                  driveTargetSystem && (
                    <ExternalSystemTreeView
                      matterId={matter_id}
                      targetSystem={driveTargetSystem}
                    />
                  )}
              </div>
            </div>
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={80} minSize={50}>
            <div className="h-full w-full overflow-hidden">
              <DocumentInspector
                blobs={blobs}
                matterId={matter_id}
                matterName={matter?.name}
              />
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </DocumentTreeProvider>
  );
};

// Component to handle blob focusing from URL params
const BlobFocusHandler = ({
  blobs,
  searchParams,
  setSearchParams,
}: {
  blobs?: Blob[];
  searchParams: URLSearchParams;
  setSearchParams: (params: URLSearchParams) => void;
}) => {
  const { setActiveFileAndSelect, nodeMaps, expandFolders } = useDocumentTree();
  const [hasFocused, setHasFocused] = useState(false);

  useEffect(() => {
    const blobId = searchParams.get('blob_id');

    // Only focus once when blobs load and blob_id param exists
    if (blobId && blobs && !hasFocused) {
      const targetBlob = blobs.find((b) => b.id === blobId);

      if (targetBlob) {
        // Find the path to this blob's collection and expand all parent folders
        const collectionId = targetBlob.collection_id;
        const parentIds: string[] = [];

        // Walk up the tree from the blob's collection to root
        let currentId = collectionId;
        while (currentId && nodeMaps.idToNodeMap.has(currentId)) {
          parentIds.push(currentId);
          const node = nodeMaps.idToNodeMap.get(currentId);
          currentId = node?.parent_id || '';
        }

        // Expand all parent folders
        if (parentIds.length > 0) {
          expandFolders(parentIds);
        }

        // Select and focus the blob
        setActiveFileAndSelect(blobId);

        // Mark as focused and remove the param
        setHasFocused(true);
        searchParams.delete('blob_id');
        setSearchParams(searchParams);
      }
    }
  }, [
    blobs,
    searchParams,
    setSearchParams,
    setActiveFileAndSelect,
    nodeMaps,
    expandFolders,
    hasFocused,
  ]);

  return null;
};

export default DocumentExplorer;

// Component to render expand/collapse toggle button for the tree
const TreeExpandCollapseButtons = () => {
  const { expandAllFolders, collapseAllFolders, expandedFolders, nodeMaps } =
    useDocumentTree();

  // Check if all folders are expanded
  const allFolderIds = Array.from(nodeMaps.idToNodeMap.keys());
  const isAllExpanded =
    allFolderIds.length > 0 &&
    allFolderIds.every((id) => expandedFolders.has(id));

  const handleToggle = () => {
    if (isAllExpanded) {
      collapseAllFolders();
    } else {
      expandAllFolders();
    }
  };

  return (
    <div className="flex items-center py-1">
      <Button
        variant="ghost"
        size="sm"
        className="h-5 text-xs  gap-1 pl-0"
        onClick={handleToggle}
      >
        {isAllExpanded ? (
          <>
            <ChevronsDownUp className="h-3 w-3" />
            Collapse All
          </>
        ) : (
          <>
            <ChevronsUpDown className="h-3 w-3" />
            Expand All
          </>
        )}
      </Button>
    </div>
  );
};
